/**
 * Note Text Extractor Utility
 * 
 * Extracts human-readable text from note content for backup purposes.
 * Used when closing a note to create a searchable text snapshot.
 */

/**
 * Extract human-readable text from note content
 * @param {Object} noteContent - The note content object (fields or sections)
 * @param {Object} noteInfo - Note metadata (client_name, template_name, etc.)
 * @returns {string} Human-readable text representation
 */
export const extractNoteTextContent = (noteContent, noteInfo) => {
  void 0 && console.log('[extractNoteTextContent] Input noteContent:', noteContent);
  void 0 && console.log('[extractNoteTextContent] Input noteInfo:', noteInfo);
  
  const lines = [];
  
  // Add header info
  if (noteInfo?.client_name) {
    lines.push(`Client: ${noteInfo.client_name}`);
  }
  if (noteInfo?.template_name || noteInfo?.form_name) {
    lines.push(`Form: ${noteInfo.template_name || noteInfo.form_name}`);
  }
  if (noteInfo?.date_of_service) {
    lines.push(`Date of Service: ${String(noteInfo.date_of_service).slice(0, 10)}`);
  }
  if (noteInfo?.status) {
    lines.push(`Status: ${noteInfo.status}`);
  }
  lines.push(`Date: ${new Date().toLocaleString()}`);
  lines.push('---');
  const headerCount = lines.length;
  
  // Extract field values
  const extractFieldValue = (field) => {
    if (!field) return null;
    const label = field.label || field.id || 'Unknown Field';
    let value = field.value;
    
    // Skip section headers and empty values
    if (field.type === 'section_header' || field.type === 'divider') return null;
    if (value === undefined || value === null || value === '') return null;
    
    // Handle arrays
    if (Array.isArray(value)) {
      value = value.filter(v => v).join(', ');
    }
    
    // Handle objects (like checkbox groups)
    if (typeof value === 'object' && !Array.isArray(value)) {
      const parts = [];
      for (const [k, v] of Object.entries(value)) {
        if (v === true) parts.push(k);
        else if (v && typeof v === 'string') parts.push(`${k}: ${v}`);
      }
      value = parts.join(', ');
    }
    
    if (!value || (typeof value === 'string' && !value.trim())) return null;
    
    return `${label}: ${value}`;
  };
  
  // Process fields array (direct content structure)
  if (noteContent?.fields && Array.isArray(noteContent.fields)) {
    void 0 && console.log('[extractNoteTextContent] Processing fields array, count:', noteContent.fields.length);
    for (const field of noteContent.fields) {
      const line = extractFieldValue(field);
      if (line) lines.push(line);
    }
  }
  
  // Process sections array (grouped content structure)
  if (noteContent?.sections && Array.isArray(noteContent.sections)) {
    void 0 && console.log('[extractNoteTextContent] Processing sections array, count:', noteContent.sections.length);
    for (const section of noteContent.sections) {
      if (section.title) {
        lines.push('');
        lines.push(`=== ${section.title} ===`);
      }
      if (section.fields && Array.isArray(section.fields)) {
        for (const field of section.fields) {
          const line = extractFieldValue(field);
          if (line) lines.push(line);
        }
      }
    }
  }
  
  // Fallback: If no fields/sections but content has text properties, try to extract
  if (lines.length <= headerCount && noteContent) {  // Only header lines present
    void 0 && console.log('[extractNoteTextContent] Falling back to raw content extraction');
    // Try to stringify raw content for backup
    try {
      const rawText = JSON.stringify(noteContent, null, 2);
      if (rawText && rawText.length > 10) {
        lines.push('[Raw Content]');
        lines.push(rawText.substring(0, 5000));  // Limit size
      }
    } catch (e) {
      console.warn('[extractNoteTextContent] Failed to stringify raw content:', e);
    }
  }
  
  const result = lines.join('\n');
  void 0 && console.log('[extractNoteTextContent] Output length:', result.length);
  return result;
};

export default extractNoteTextContent;
